import { useEffect, useMemo, useState } from "react";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import { useGoogleSharedDrives } from "./use-google-drive-browser";

export interface DriveBreadcrumbItem {
  id: string;
  name: string;
  driveId?: string;
}

interface UseGoogleDriveBreadcrumbsOptions {
  enabled?: boolean;
  targetFolderId?: string; // folder we want to show
  driveIdHint?: string; // if you already know the drive id
}

interface UseGoogleDriveBreadcrumbsResult {
  breadcrumbs: DriveBreadcrumbItem[];
  parentName?: string;
  driveId?: string;
  driveName?: string;
  isLoading: boolean;
}

export function useGoogleDriveBreadcrumbs(
  options: UseGoogleDriveBreadcrumbsOptions = {}
): UseGoogleDriveBreadcrumbsResult {
  const { enabled = true, targetFolderId, driveIdHint } = options;
  const { session } = useUniversalAuth();
  const [breadcrumbs, setBreadcrumbs] = useState<DriveBreadcrumbItem[]>([]);
  const [parentName, setParentName] = useState<string | undefined>(undefined);
  const [derivedDriveId, setDerivedDriveId] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { data: drivesListing } = useGoogleSharedDrives({ enabled });

  const driveName = useMemo(() => {
    const id = driveIdHint || derivedDriveId;
    if (!id) return undefined;
    const match = drivesListing?.drives?.find((d) => d.id === id);
    return match?.name;
  }, [driveIdHint, derivedDriveId, drivesListing]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!enabled || !targetFolderId || !session?.access_token) {
        setBreadcrumbs([]);
        setParentName(undefined);
        return;
      }
      setIsLoading(true);
      try {
        const headers = { Authorization: `Bearer ${session.access_token}` } as Record<
          string,
          string
        >;
        const makeUrl = (id: string) =>
          `/api/unifiedtime/getDrive/folderInfo?id=${encodeURIComponent(id)}`;
        const chain: Array<{ id: string; name: string; driveId?: string; parents?: string[] }> = [];
        let currentId: string | undefined = targetFolderId;
        let guard = 0;
        let firstDriveId: string | undefined = undefined;

        while (currentId && guard < 12) {
          const resp: Response = await fetch(makeUrl(currentId), { headers });
          if (!resp.ok) break;
          const info: any = await resp.json();
          chain.unshift({
            id: info.id as string,
            name: info.name as string,
            driveId: info.driveId as string | undefined,
            parents: info.parents as string[] | undefined,
          });
          if (!firstDriveId && info.driveId) firstDriveId = info.driveId as string;
          currentId =
            Array.isArray(info.parents) && info.parents.length > 0 ? info.parents[0] : undefined;
          guard++;
        }

        const crumbs: DriveBreadcrumbItem[] = [];
        crumbs.push({ id: "drives", name: "Shared Drives" });
        const finalDriveId = driveIdHint || firstDriveId;
        if (finalDriveId && driveName) {
          crumbs.push({ id: finalDriveId, name: driveName, driveId: finalDriveId });
        }
        for (const node of chain) {
          crumbs.push({ id: node.id, name: node.name, driveId: node.driveId });
        }

        if (!cancelled) {
          setDerivedDriveId(finalDriveId);
          setBreadcrumbs(crumbs);
          setParentName(chain.length > 0 ? chain[chain.length - 1].name : undefined);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [enabled, targetFolderId, session?.access_token, driveIdHint, driveName]);

  return {
    breadcrumbs,
    parentName,
    driveId: driveIdHint || derivedDriveId,
    driveName,
    isLoading,
  };
}
